/**
 * Stripe Payment Link Service
 * Handles redirecting to Stripe Payment Links and verifying payments
 */

import { logger } from '../utils/logger';

// Get Payment Links from environment variables (daily plan removed)
const PAYMENT_LINKS = {
  'monthly-global': import.meta.env.VITE_STRIPE_PAYMENT_LINK_MONTHLY_GLOBAL,
  'monthly-all': import.meta.env.VITE_STRIPE_PAYMENT_LINK_MONTHLY_ALL
};

// Debug: Log Payment Links (only in development)
logger.log('Stripe Payment Links loaded:', {
  'monthly-global': PAYMENT_LINKS['monthly-global'] ? '✓ Configured' : '✗ Missing',
  'monthly-all': PAYMENT_LINKS['monthly-all'] ? '✓ Configured' : '✗ Missing'
});

/**
 * Redirect user to Stripe Payment Link
 * @param {string} planType - Plan type: 'monthly-global' or 'monthly-all'
 * @param {string} userId - Firebase user ID (optional, for tracking)
 * @param {string} returnUrl - URL to redirect to after payment (optional, defaults to /account)
 * @returns {object} - Result object with success status
 */
export const redirectToPayment = (planType, userId = null, returnUrl = null) => {
  logger.log('redirectToPayment called with:', { planType, userId, returnUrl });
  logger.log('Available Payment Links:', PAYMENT_LINKS);
  
  const paymentLink = PAYMENT_LINKS[planType];
  
  if (!paymentLink) {
    logger.error('Payment link not found for plan:', planType);
    logger.error('Available plan types:', Object.keys(PAYMENT_LINKS));
    return { success: false, error: 'Payment link not configured' };
  }
  
  logger.log('Redirecting to Stripe Payment Link:', paymentLink);

  // Build URL with parameters
  const urlParams = new URLSearchParams();
  
  // Add user ID as client_reference_id for tracking
  if (userId) {
    urlParams.append('client_reference_id', userId);
  }
  
  // Add redirect URL if provided
  if (returnUrl) {
    urlParams.append('redirect_on_completion', 'always');
    urlParams.append('redirect_to', returnUrl);
  } else {
    // Default to /account if no return URL specified
    const defaultReturnUrl = encodeURIComponent(`${window.location.origin}/account`);
    urlParams.append('redirect_on_completion', 'always');
    urlParams.append('redirect_to', defaultReturnUrl);
  }

  // Construct final payment link with all parameters
  const separator = paymentLink.includes('?') ? '&' : '?';
  const finalLink = `${paymentLink}${separator}${urlParams.toString()}`;

  logger.log('Final payment link:', finalLink);

  // Mark that we're redirecting to Stripe so AccountPage knows to sync on return
  try {
    sessionStorage.setItem('redirecting_to_stripe', Date.now().toString());
  } catch (e) {
    logger.warn('Could not set redirecting_to_stripe', e);
  }

  // Redirect to Stripe Payment Link
  window.location.href = finalLink;
  
  return { success: true };
};

/**
 * Verify payment by email when session_id is missing
 * This is a fallback mechanism when redirect URL doesn't include session_id
 * @param {string} userId - Firebase user ID
 * @param {string} userEmail - User's email address
 * @returns {Promise<object>} - Payment verification result
 */
export const verifyPaymentByEmail = async (userId, userEmail) => {
  try {
    if (!userId || !userEmail) {
      return { success: false, error: 'User ID and email are required' };
    }

    logger.log('verifyPaymentByEmail called', { userId, userEmail });

    const functionUrl = import.meta.env.VITE_CLOUD_FUNCTION_URL || 'http://localhost:5001';
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'altradenative';
    
    let verifyByEmailUrl;
    if (functionUrl.includes('localhost')) {
      const region = 'us-central1';
      verifyByEmailUrl = `${functionUrl}/${projectId}/${region}/verifyPaymentByEmail`;
    } else {
      verifyByEmailUrl = `${functionUrl}/verifyPaymentByEmail`;
    }

    logger.log('Calling verifyPaymentByEmail function:', verifyByEmailUrl);

    const response = await fetch(verifyByEmailUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, userEmail })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      return { success: false, error: errorData.error || 'Failed to verify payment by email' };
    }

    const data = await response.json();
    
    // The backend now handles verification directly, so just return the result
    return data;
  } catch (error) {
    logger.error('Error in verifyPaymentByEmail:', error);
    return { success: false, error: error.message || 'Failed to verify payment by email' };
  }
};

export const verifyPayment = async (sessionId, userId = null) => {
  try {
    if (!sessionId) {
      logger.error('verifyPayment: Session ID is required');
      return { success: false, error: 'Session ID is required' };
    }

    logger.log('verifyPayment called', { sessionId, userId });

    // Detect if we're in production (not localhost)
    const isProduction = !window.location.hostname.includes('localhost') && 
                         !window.location.hostname.includes('127.0.0.1');

    // Get the Cloud Function URL from environment variables
    let functionUrl = import.meta.env.VITE_CLOUD_FUNCTION_URL;
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;

    // Auto-detect Cloud Function URL in production if not set
    if (isProduction && !functionUrl) {
      // Try to construct URL from project ID
      if (projectId) {
        const region = 'us-central1'; // Default Firebase Functions region
        functionUrl = `https://${region}-${projectId}.cloudfunctions.net`;
        logger.warn('⚠️ VITE_CLOUD_FUNCTION_URL not set, auto-detecting from project ID:', {
          functionUrl,
          projectId,
          note: 'Consider setting VITE_CLOUD_FUNCTION_URL explicitly in your production environment'
        });
      } else {
        logger.error('❌ CRITICAL: VITE_CLOUD_FUNCTION_URL and VITE_FIREBASE_PROJECT_ID are not set in production!', {
          hostname: window.location.hostname,
          message: 'Cannot determine Cloud Function URL. Please set VITE_CLOUD_FUNCTION_URL in your production environment variables.'
        });
        return {
          success: false,
          error: 'Cloud Function URL is not configured for production. Please contact support.'
        };
      }
    }
    
    // Default to localhost for development
    if (!functionUrl) {
      functionUrl = 'http://localhost:5001';
    }

    // Validate environment configuration for production
    if (isProduction && functionUrl.includes('localhost')) {
      logger.error('❌ CRITICAL: VITE_CLOUD_FUNCTION_URL is pointing to localhost in production!', {
        hostname: window.location.hostname,
        functionUrl,
        message: 'The Cloud Function URL is still pointing to localhost. Please set VITE_CLOUD_FUNCTION_URL in your production environment variables.'
      });
      return {
        success: false,
        error: 'Cloud Function URL is not configured for production. Please contact support or use the static login link.'
      };
    }
    
    // Construct the function URL
    // For local emulator: http://localhost:5001/{project-id}/{region}/verifyPayment
    // For production: https://{region}-{project-id}.cloudfunctions.net/verifyPayment
    let verifyUrl;
    if (functionUrl.includes('localhost')) {
      // Local emulator
      const region = 'us-central1'; // Default region
      verifyUrl = `${functionUrl}/${projectId || 'default'}/${region}/verifyPayment`;
    } else {
      // Production - ensure URL doesn't have trailing slash
      const baseUrl = functionUrl.replace(/\/$/, '');
      verifyUrl = `${baseUrl}/verifyPayment`;
    }

    logger.log('Calling verifyPayment function:', verifyUrl);
    logger.log('Request body:', { sessionId, userId });
    logger.log('Environment check:', {
      functionUrl,
      projectId,
      isLocalhost: functionUrl.includes('localhost')
    });

    let response;
    try {
      response = await fetch(verifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId, userId })
      });
    } catch (fetchError) {
      logger.error('Fetch error details:', {
        name: fetchError.name,
        message: fetchError.message,
        stack: fetchError.stack
      });
      throw new Error(`Network error: Cannot reach Cloud Function at ${verifyUrl}. Is the Firebase Functions emulator running? Error: ${fetchError.message}`);
    }

    logger.log('verifyPayment response status:', response.status);
    logger.log('verifyPayment response headers:', Object.fromEntries(response.headers.entries()));

    // Get response text first to see what we're dealing with
    const responseText = await response.text();
    logger.log('verifyPayment raw response:', responseText);

    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        // If response isn't JSON, use the text as error
        errorData = { error: responseText || `HTTP ${response.status}: ${response.statusText}` };
      }
      
      logger.error('verifyPayment error details:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
        url: verifyUrl,
        sessionId,
        userId
      });
      
      return { 
        success: false, 
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        details: errorData
      };
    }

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', e);
      return {
        success: false,
        error: 'Invalid response from server',
        details: { rawResponse: responseText }
      };
    }

    logger.log('verifyPayment success:', data);
    return data;
  } catch (error) {
    logger.error('Error verifying payment (network/exception):', error);
    logger.error('Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    return {
      success: false,
      error: error.message || 'Network error: Failed to verify payment. Is the Cloud Function running?',
      details: { 
        type: error.name,
        message: error.message 
      }
    };
  }
};

/**
 * Sync subscription data from Stripe to Firebase
 * This fetches the latest subscription data from Stripe and updates Firebase
 * @param {string} userId - Firebase user ID
 * @returns {Promise<object>} - Sync result with updated subscription data
 */
export const syncSubscription = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    // Detect if we're running in production (not localhost)
    const isProduction = !window.location.hostname.includes('localhost') &&
                         !window.location.hostname.includes('127.0.0.1');

    // Base Cloud Functions URL
    let functionUrl = import.meta.env.VITE_CLOUD_FUNCTION_URL;
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'altradenative';

    // Auto-detect Cloud Function URL in production if not explicitly set
    if (isProduction && !functionUrl) {
      if (projectId) {
        const region = 'us-central1';
        functionUrl = `https://${region}-${projectId}.cloudfunctions.net`;
        logger.warn('⚠️ VITE_CLOUD_FUNCTION_URL not set for syncSubscription, auto-detecting from project ID:', {
          functionUrl,
          projectId,
          note: 'Consider setting VITE_CLOUD_FUNCTION_URL explicitly in your production environment'
        });
      } else {
        logger.error('❌ CRITICAL: VITE_CLOUD_FUNCTION_URL and VITE_FIREBASE_PROJECT_ID are not set in production for syncSubscription!', {
          hostname: window.location.hostname,
          message: 'Cannot determine Cloud Function URL. Please set VITE_CLOUD_FUNCTION_URL in your production environment variables.'
        });
        return {
          success: false,
          error: 'Cloud Function URL is not configured for production. Please contact support.'
        };
      }
    }

    // Default to localhost for development
    if (!functionUrl) {
      functionUrl = 'http://localhost:5001';
    }

    // Validate environment configuration for production
    if (isProduction && functionUrl.includes('localhost')) {
      logger.error('❌ CRITICAL: VITE_CLOUD_FUNCTION_URL is pointing to localhost in production for syncSubscription!', {
        hostname: window.location.hostname,
        functionUrl,
        message: 'The Cloud Function URL is still pointing to localhost. Please set VITE_CLOUD_FUNCTION_URL in your production environment variables.'
      });
      return {
        success: false,
        error: 'Cloud Function URL is not configured for production. Please contact support.'
      };
    }
    
    let syncUrl;
    if (functionUrl.includes('localhost')) {
      const region = 'us-central1';
      syncUrl = `${functionUrl}/${projectId || 'default'}/${region}/syncSubscription`;
    } else {
      const baseUrl = functionUrl.replace(/\/$/, '');
      syncUrl = `${baseUrl}/syncSubscription`;
    }

    logger.log('Syncing subscription from Stripe:', syncUrl);

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Debug-Session-Id': '60e2e9',
      },
      body: JSON.stringify({
        sessionId: '60e2e9',
        runId: 'initial',
        hypothesisId: 'H3',
        location: 'src/services/stripeService.js:299',
        message: 'syncSubscription request',
        data: {
          userId,
          syncUrl,
          hasFunctionUrlEnv: !!import.meta.env.VITE_CLOUD_FUNCTION_URL,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    const response = await fetch(syncUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to sync subscription' }));
      return { success: false, error: errorData.error || 'Failed to sync subscription' };
    }

    const data = await response.json();
    logger.log('Subscription synced successfully:', data);
    return data;
  } catch (error) {
    logger.error('Error syncing subscription:', error);
    return { success: false, error: error.message || 'Failed to sync subscription' };
  }
};

/**
 * Create Stripe Customer Portal session
 * This allows users to manage their subscriptions through Stripe's hosted portal
 * @param {string} userId - Firebase user ID
 * @returns {Promise<object>} - Portal session result with URL
 */
/**
 * Create a portal session for the Stripe Customer Portal
 * 
 * This function follows Stripe's recommended integration pattern:
 * https://docs.stripe.com/customer-management/integrate-customer-portal
 * 
 * Instead of using a static link, we create a dynamic portal session via the API.
 * This provides secure, temporary session URLs with proper customer authentication.
 * 
 * @param {string} userId - Firebase user ID
 * @returns {Promise<object>} - Portal session result { success: boolean, url?: string, error?: string }
 */
export const createPortalSession = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    // Get the Cloud Function URL from environment variables
    let functionUrl = import.meta.env.VITE_CLOUD_FUNCTION_URL;
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'altradenative';
    
    // Detect if we're in production (not localhost)
    const isProduction = !window.location.hostname.includes('localhost') && 
                         !window.location.hostname.includes('127.0.0.1');
    
    // Auto-detect Cloud Function URL in production if not set
    if (isProduction && !functionUrl) {
      // Try to construct URL from project ID
      if (projectId) {
        const region = 'us-central1'; // Default Firebase Functions region
        functionUrl = `https://${region}-${projectId}.cloudfunctions.net`;
        logger.warn('⚠️ VITE_CLOUD_FUNCTION_URL not set, auto-detecting from project ID:', {
          functionUrl,
          projectId,
          note: 'Consider setting VITE_CLOUD_FUNCTION_URL explicitly in your production environment'
        });
      } else {
        logger.error('❌ CRITICAL: VITE_CLOUD_FUNCTION_URL and VITE_FIREBASE_PROJECT_ID are not set in production!', {
          hostname: window.location.hostname,
          message: 'Cannot determine Cloud Function URL. Please set VITE_CLOUD_FUNCTION_URL in your production environment variables.'
        });
        return {
          success: false,
          error: 'Cloud Function URL is not configured for production. Please contact support or use the static login link.'
        };
      }
    }
    
    // Default to localhost for development
    if (!functionUrl) {
      functionUrl = 'http://localhost:5001';
    }
    
    // Validate environment configuration for production
    if (isProduction && functionUrl.includes('localhost')) {
      logger.error('❌ CRITICAL: VITE_CLOUD_FUNCTION_URL is pointing to localhost in production!', {
        hostname: window.location.hostname,
        functionUrl,
        message: 'The Cloud Function URL is still pointing to localhost. Please set VITE_CLOUD_FUNCTION_URL in your production environment variables.'
      });
      return {
        success: false,
        error: 'Cloud Function URL is not configured for production. Please contact support or use the static login link.'
      };
    }
    
    // Check for placeholder values in production
    if (isProduction && functionUrl && functionUrl.includes('yourproject')) {
      logger.error('❌ CRITICAL: VITE_CLOUD_FUNCTION_URL contains placeholder value "yourproject"!', {
        hostname: window.location.hostname,
        functionUrl,
        message: 'Please replace "yourproject" with your actual Firebase project ID in .env.production'
      });
      return {
        success: false,
        error: 'Cloud Function URL contains placeholder value. Please update VITE_CLOUD_FUNCTION_URL with your actual project ID.'
      };
    }
    
    // Construct the function URL
    // For local emulator: http://localhost:5001/{project-id}/{region}/createPortalSession
    // For production: https://{region}-{project-id}.cloudfunctions.net/createPortalSession
    let portalUrl;
    if (functionUrl.includes('localhost')) {
      // Local emulator
      const region = 'us-central1'; // Default region
      portalUrl = `${functionUrl}/${projectId}/${region}/createPortalSession`;
    } else {
      // Production - ensure URL doesn't have trailing slash
      const baseUrl = functionUrl.replace(/\/$/, '');
      portalUrl = `${baseUrl}/createPortalSession`;
    }

    // Include return URL - when customer finishes in portal, they'll return here
    // The portal=returned parameter triggers subscription sync on return
    const returnUrl = `${window.location.origin}/account?portal=returned`;

    // Enhanced logging for debugging (only in development)
    logger.log('📡 Creating Stripe Customer Portal session...', { 
      userId, 
      returnUrl,
      portalUrl,
      isProduction,
      functionUrlConfigured: !!import.meta.env.VITE_CLOUD_FUNCTION_URL,
      functionUrl: functionUrl,
      projectId: projectId,
      hostname: window.location.hostname,
      // Log the actual environment variable value (first 30 chars for security)
      envFunctionUrl: import.meta.env.VITE_CLOUD_FUNCTION_URL ? 
        import.meta.env.VITE_CLOUD_FUNCTION_URL.substring(0, 30) + '...' : 'NOT SET'
    });

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Debug-Session-Id': '60e2e9',
      },
      body: JSON.stringify({
        sessionId: '60e2e9',
        runId: 'initial',
        hypothesisId: 'H1',
        location: 'src/services/stripeService.js:356',
        message: 'createPortalSession request',
        data: {
          userId,
          portalUrl,
          isProduction,
          hasFunctionUrlEnv: !!import.meta.env.VITE_CLOUD_FUNCTION_URL,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    let response;
    const requestBody = { userId, returnUrl };
    try {
      response = await fetch(portalUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
    } catch (fetchError) {
      // Network error (CORS, connection refused, etc.)
      logger.error('❌ Network error calling portal function:', {
        error: fetchError.message,
        portalUrl,
        isProduction,
        functionUrlConfigured: !!import.meta.env.VITE_CLOUD_FUNCTION_URL
      });
      
      // Provide helpful error message based on environment
      if (isProduction) {
        return {
          success: false,
          error: `Cannot connect to Cloud Function. Please verify VITE_CLOUD_FUNCTION_URL is set correctly. Error: ${fetchError.message}`
        };
      } else {
        return {
          success: false,
          error: `Network error: ${fetchError.message}. Is the Firebase Functions emulator running?`
        };
      }
    }

    logger.log('📥 Portal function response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      contentType: response.headers.get('content-type')
    });

    if (!response.ok) {
      let errorData;
      let responseText;
      try {
        responseText = await response.text();
        errorData = JSON.parse(responseText);
      } catch (parseError) {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      
      logger.error('Portal function error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
        portalUrl
      });
      
      return { 
        success: false, 
        error: errorData.error || `Failed to create portal session (Status: ${response.status})` 
      };
    }

    let data;
    try {
      const responseText = await response.text();
      data = JSON.parse(responseText);
    } catch (parseError) {
      logger.error('Failed to parse portal response as JSON:', parseError);
      return {
        success: false,
        error: 'Invalid response from server. Please try again or use the static login link.'
      };
    }
    
    // Verify we got a valid session URL (not a login page)
    if (data.success && data.url) {
      if (data.url.includes('/p/login/')) {
        logger.error('❌ ERROR: Received login page URL instead of session URL', {
          url: data.url
        });
        return {
          success: false,
          error: 'Failed to create authenticated portal session. The server returned a login page URL instead of a session URL. Please check your Stripe customer ID.'
        };
      }
      
      if (!data.url.includes('/p/session/')) {
        logger.warn('⚠️ WARNING: Portal URL format may be unexpected', {
          url: data.url
        });
      }
      
      logger.log('✅ Portal session created successfully:', {
        url: data.url.substring(0, 50) + '...', // Log partial URL for security
        isSessionUrl: data.url.includes('/p/session/')
      });
    }
    
    return data;
  } catch (error) {
    logger.error('❌ Error creating portal session:', {
      error: error.message,
      stack: error.stack,
      name: error.name
    });
    return {
      success: false,
      error: error.message || 'Unexpected error creating portal session. Please try again or use the static login link.'
    };
  }
};

